import { z } from 'zod';
import { UploadTarget } from './files.js';
import { PersonRef } from './people.js';
/**
 * Importing history from Slack (docs/adr/0017-slack-import.md). An owner uploads the export ZIP, the
 * worker reads it and proposes a plan (who is who, which channels to create), the owner adjusts the
 * plan and starts the import. That imports channels, messages, replies, reactions and mentions. Files are
 * a second pass: the export only links to them, so the owner gives Chapsi a Slack token with files:read
 * and the worker downloads each one into the message it belongs to.
 */
/** Slack exports can be large. One upload, up to 4 GB (S3 allows 5 GB in a single PUT). */
export const MAX_IMPORT_BYTES = 4 * 1024 * 1024 * 1024;
export const ImportStatus = z.enum(['awaiting_upload', 'analyzing', 'ready', 'running', 'done', 'failed', 'cancelled']);
export const ImportPerson = z.object({
    slackId: z.string(),
    name: z.string(),
    email: z.string().nullable(),
    isBot: z.boolean(),
    deleted: z.boolean(),
    /** The Chapsi member their messages are attributed to; null keeps their name with "from Slack". */
    userId: z.uuid().nullable(),
    messageCount: z.number().int(),
});
export const ImportChannelAction = z.enum(['create', 'skip']);
export const ImportChannelType = z.enum(['client', 'internal']);
export const ImportChannel = z.object({
    slackId: z.string(),
    slackName: z.string(),
    kind: z.enum(['public', 'private', 'dm', 'group_dm']),
    archived: z.boolean(),
    memberCount: z.number().int(),
    messageCount: z.number().int(),
    action: ImportChannelAction,
    /** Public and private Slack channels become client or internal channels; DMs stay DMs. */
    type: ImportChannelType,
    clientId: z.uuid().nullable(),
    /** The Chapsi channel name to create (lowercase, hyphens, unique). */
    channelName: z.string(),
    /** Set once imported. */
    channelId: z.uuid().nullable(),
});
export const ImportProgress = z.object({
    channelsDone: z.number().int(),
    channelsTotal: z.number().int(),
    messages: z.number().int(),
    replies: z.number().int(),
    reactions: z.number().int(),
    skipped: z.number().int(),
});
export const ImportFilesStatus = z.enum(['none', 'queued', 'running', 'done', 'failed', 'cancelled']);
/** The files pass. Counts are per file attached to a message in the export. */
export const ImportFiles = z.object({
    status: ImportFilesStatus,
    /** Files linked from imported messages. */
    total: z.number().int(),
    /** Waiting to be downloaded (or to be retried). */
    pending: z.number().int(),
    done: z.number().int(),
    /** Not downloadable: deleted in Slack, hidden by Slack's plan limits, a link to another service, a program, or too large. */
    skipped: z.number().int(),
    failed: z.number().int(),
    /** Bytes still to download, as Slack reported them. */
    pendingBytes: z.number().int(),
    /** Shown when status is failed. */
    error: z.string().nullable(),
});
export const Import = z.object({
    id: z.uuid(),
    provider: z.literal('slack'),
    status: ImportStatus,
    fileName: z.string(),
    sizeBytes: z.number().int(),
    createdBy: PersonRef.nullable(),
    people: z.array(ImportPerson),
    channels: z.array(ImportChannel),
    progress: ImportProgress,
    files: ImportFiles,
    /** Shown when status is failed. Never contains message text. */
    error: z.string().nullable(),
    createdAt: z.string(),
    startedAt: z.string().nullable(),
    finishedAt: z.string().nullable(),
});
export const ImportList = z.object({ items: z.array(Import.omit({ people: true, channels: true })) });
export const CreateImportInput = z.object({
    fileName: z.string().trim().min(1).max(255).regex(/\.zip$/i, 'Choose the .zip file Slack gave you'),
    size: z.number().int().min(1).max(MAX_IMPORT_BYTES),
});
export const CreateImportResponse = z.object({ import: Import, upload: UploadTarget });
export const UpdateImportPlanInput = z.object({
    people: z.array(z.object({ slackId: z.string().max(32), userId: z.uuid().nullable() })).max(20_000),
    channels: z
        .array(z.object({
        slackId: z.string().max(32),
        action: ImportChannelAction,
        type: ImportChannelType,
        clientId: z.uuid().nullable(),
        channelName: z.string().max(80).regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Use lowercase letters, numbers and hyphens'),
    }))
        .max(20_000),
});
/**
 * A Slack token that can read files: a user token (xoxp-) with the files:read user scope is the simple
 * path; a bot token (xoxb-) only reaches files in channels the bot was added to. Used for this import
 * only, stored encrypted under the workspace's key, and deleted when the files pass ends.
 */
export const SLACK_TOKEN = /^xox[pbe]-[A-Za-z0-9-]{10,250}$/;
export const StartImportFilesInput = z.object({
    slackToken: z.string().trim().regex(SLACK_TOKEN, 'Paste the token Slack shows, starting xoxp- (or xoxb-)'),
});
//# sourceMappingURL=imports.js.map